import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search, UserPlus, Check } from 'lucide-react';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { orderLabel } from '../utils/itemName';

function PaymentModal({ order, orders, isBulk, label, onComplete, onClose }) {
  const { t, language } = useLanguage();
  const [paymentType, setPaymentType] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percentage'
  // Kept as raw strings so the field can actually be emptied while typing.
  // Coercing to a number on every keystroke meant clearing the box snapped it
  // straight back to 0, and digits piled up behind a stuck leading zero.
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  // Who owes the money when the bill goes out on credit
  const [dueName, setDueName] = useState('');
  const [dueMobile, setDueMobile] = useState('');

  // Credit customers already on file. A regular is picked from this list rather
  // than typed again, which is what keeps one person from ending up with three
  // separate slates under three spellings of their name.
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [customersError, setCustomersError] = useState(null);

  // Calculate total amount based on whether it's bulk or single order
  const totalAmount = isBulk
    ? orders?.reduce((sum, current) => sum + (current.totalAmount || 0), 0) || 0
    : order?.totalAmount || 0;

  // Clamped here as well as on the inputs - min/max don't stop someone typing an
  // out-of-range figure, and the server rejects a percentage above 100
  const discountAmountValue = Math.min(Math.max(parseFloat(discountAmount) || 0, 0), totalAmount);
  const discountPercentageValue = Math.min(Math.max(parseFloat(discountPercentage) || 0, 0), 100);

  const appliedDiscount = discountType === 'percentage'
    ? (totalAmount * discountPercentageValue) / 100
    : discountAmountValue;

  const finalAmount = totalAmount - appliedDiscount;

  const isDue = paymentType === 'due';

  /*
    Deliberately no "is this component still mounted?" guard.

    Two earlier versions of this deadlocked on exactly that. The first listed the
    loading flag as a dependency, so setting it re-ran the effect, whose cleanup
    marked the in-flight request cancelled. The second used a mounted ref, which
    StrictMode's mount/unmount/mount cycle latched to false and never reset.
    Both discarded the response AND skipped the .finally that clears the flag, so
    the picker sat on "Loading customers..." for ever.

    React 18 removed the setState-on-unmounted warning those guards existed for,
    so there is nothing left to guard against - and the guard is what kept
    breaking. The only ref here is the one that stops a second fetch, and it can
    never strand the loading flag because it is not consulted in the response.
  */
  const requestedRef = useRef(false);

  const loadCustomers = useCallback(() => {
    requestedRef.current = true;
    setCustomersError(null);
    setCustomersLoading(true);

    api
      // The shared client sets no timeout, so a stalled connection would hang
      // here indefinitely - which looks identical to the bugs above.
      .get('/api/customers', { timeout: 15000 })
      .then((response) => {
        setCustomers(response.data);
        // Nobody on file yet, so there is nothing to pick from - go straight to
        // the entry fields instead of showing an empty search box.
        if (response.data.length === 0) setAddingNew(true);
      })
      .catch((error) => {
        // Let them try again by switching away and back, or with Retry.
        requestedRef.current = false;
        // The picker is a convenience: typing the customer in by hand still
        // takes the payment. But say so rather than silently dropping to the
        // manual fields, or a 404 from a backend that was never restarted looks
        // like the feature is simply missing.
        setCustomersError(
          error?.response?.status === 404
            ? t('Customer list needs a newer backend - enter the details by hand for now.')
            : t('Could not load saved customers - enter the details by hand.')
        );
        setAddingNew(true);
      })
      .finally(() => setCustomersLoading(false));
  }, [t]);

  // Loaded when credit is first chosen, not on mount - most bills are cash and
  // never need this list.
  useEffect(() => {
    if (!isDue || requestedRef.current) return;
    loadCustomers();
  }, [isDue, loadCustomers]);

  const matchingCustomers = customers.filter((customer) => {
    const query = customerSearch.trim().toLowerCase();
    if (!query) return true;

    if (customer.name.toLowerCase().includes(query)) return true;

    // Numbers are stored canonically (country code trimmed), so a search typed
    // as "+977 9812345678" would match nothing against a stored "9812345678".
    // Both the raw digits and the trimmed form are tried.
    const digits = query.replace(/\D/g, '');
    if (!digits) return false;

    const trimmed = digits.length > 10 ? digits.slice(-10) : digits;
    return customer.mobile.includes(digits) || customer.mobile.includes(trimmed);
  });

  // While typing a "new" customer, spot a number that is already on file. The
  // sale would be filed against them either way - the server keys on the number
  // - so it is better to say so before the money is taken than to leave the
  // cashier thinking they created someone new.
  const typedDigits = dueMobile.replace(/\D/g, '');
  const existingMatch =
    typedDigits.length >= 7
      ? customers.find((customer) => customer.mobile === typedDigits) || null
      : null;

  const chooseCustomer = (customer) => {
    setSelectedCustomer(customer);
    setDueName(customer.name);
    setDueMobile(customer.mobile);
    setAddingNew(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setDueName('');
    setDueMobile('');
    setAddingNew(false);
    setCustomerSearch('');
  };

  // The date on a due is stamped by the server when the bill goes on account -
  // shown here only so the cashier can see what is being recorded against it
  const dueDateLabel = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  // A due with no name or no reachable number is money written off, so the
  // button stays locked until there is someone to chase
  const dueMobileDigits = dueMobile.replace(/\D/g, '');
  const dueDetailsReady = dueName.trim().length > 0 && dueMobileDigits.length >= 7;
  const canComplete = Boolean(paymentType) && (!isDue || dueDetailsReady);

  const handleComplete = async () => {
    if (!canComplete) return;

    setIsProcessing(true);
    try {
      const discountData = {
        discountAmount: discountType === 'amount' ? discountAmountValue : 0,
        discountPercentage: discountType === 'percentage' ? discountPercentageValue : 0
      };
      const dueDetails = isDue
        ? { dueCustomerName: dueName.trim(), dueCustomerMobile: dueMobileDigits }
        : null;
      await onComplete(paymentType, discountData, dueDetails);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {label
              ? t('Payment - {label}', { label })
              : t(isBulk ? 'Complete Orders' : 'Complete Order')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Order Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('Order Details')}</h3>
            {isBulk ? (
              <div className="space-y-2">
                <div className="text-sm text-gray-600 space-y-1">
                  {orders.map((line) => (
                    <div key={line._id} className="flex justify-between gap-4">
                      <span className="truncate">
                        {orderLabel(line, language)}
                        {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                      </span>
                      <span className="flex-shrink-0">Rs {(line.totalAmount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {t('Total Amount: Rs {amount}', { amount: totalAmount.toFixed(2) })}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  {orderLabel(order, language)} × {order.quantity}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {t('Total Amount: Rs {amount}', { amount: totalAmount.toFixed(2) })}
                </p>
              </div>
            )}
          </div>

          {/* Discount Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('Discount')}</h3>
            
            {/* Discount Type Selection */}
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="discountType"
                  value="amount"
                  checked={discountType === 'amount'}
                  onChange={(e) => setDiscountType(e.target.value)}
                  className="mr-2"
                />
                {t('Amount (Rs.)')}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="discountType"
                  value="percentage"
                  checked={discountType === 'percentage'}
                  onChange={(e) => setDiscountType(e.target.value)}
                  className="mr-2"
                />
                {t('Percentage (%)')}
              </label>
            </div>

            {/* Discount Input */}
            <div className="space-y-2">
              {discountType === 'amount' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Discount Amount (Rs.)')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={totalAmount}
                    step="0.01"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-500"
                    placeholder={t('Enter discount amount')}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Discount Percentage (%)')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-500"
                    placeholder={t('Enter discount percentage')}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Final Amount Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center text-lg font-semibold">
              <span>{t('Final Amount:')}</span>
              <span className="text-cafe-600">Rs {finalAmount.toFixed(2)}</span>
            </div>
            {appliedDiscount > 0 && (
              <div className="text-sm text-gray-600 mt-1">
                {t('Discount Applied: Rs {amount}', { amount: appliedDiscount.toFixed(2) })}
              </div>
            )}
          </div>

          {/* Payment Method Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('Payment Method')}</h3>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <button
                onClick={() => setPaymentType('cash')}
                className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
                  paymentType === 'cash'
                    ? 'border-cafe-500 bg-cafe-50 text-cafe-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">💵</div>
                  <div className="font-medium text-sm sm:text-base">{t('Cash')}</div>
                </div>
              </button>
              <button
                onClick={() => setPaymentType('bank')}
                className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
                  paymentType === 'bank'
                    ? 'border-cafe-500 bg-cafe-50 text-cafe-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">🏦</div>
                  <div className="font-medium text-sm sm:text-base">{t('Bank')}</div>
                </div>
              </button>
              <button
                onClick={() => setPaymentType('due')}
                className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
                  isDue
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">📒</div>
                  <div className="font-medium text-sm sm:text-base">{t('Due')}</div>
                </div>
              </button>
            </div>

            {/* Who owes it. Nothing leaves on credit without a name and a
                number - the date is stamped by the server, not typed here. */}
            {isDue && (
              <div className="border-2 border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-amber-900">{t('Due Details')}</h4>
                  <span className="text-sm text-amber-800">{dueDateLabel}</span>
                </div>

                {customersError && (
                  <div className="text-xs text-amber-900 bg-amber-100 border border-amber-300 rounded-md px-2.5 py-2 flex items-start justify-between gap-2">
                    <span>{customersError}</span>
                    <button
                      type="button"
                      onClick={loadCustomers}
                      className="flex-shrink-0 font-semibold underline hover:no-underline"
                    >
                      {t('Retry')}
                    </button>
                  </div>
                )}

                {selectedCustomer ? (
                  /* Picked from the list: shown as a confirmed choice rather
                     than editable fields, so a stray keystroke cannot fork a
                     regular's slate at the moment of taking money. */
                  <div className="bg-white border border-amber-300 rounded-md p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="font-medium text-gray-900 truncate">
                            {selectedCustomer.name}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">{selectedCustomer.mobile}</p>
                        {selectedCustomer.outstanding > 0 && (
                          <p className="text-sm text-amber-800 mt-1">
                            {t('Already owes Rs {amount} across {count} {unit}', {
                              amount: selectedCustomer.outstanding.toFixed(2),
                              count: selectedCustomer.billCount,
                              unit: t(selectedCustomer.billCount === 1 ? 'bill' : 'bills')
                            })}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={clearCustomer}
                        className="text-sm text-amber-800 underline hover:no-underline flex-shrink-0"
                      >
                        {t('Change')}
                      </button>
                    </div>
                  </div>
                ) : addingNew ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Customer Name')}
                      </label>
                      <input
                        type="text"
                        value={dueName}
                        onChange={(e) => setDueName(e.target.value)}
                        maxLength={100}
                        className="w-full px-3 py-2 border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder={t('Enter customer name')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Mobile Number')}
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={dueMobile}
                        onChange={(e) => setDueMobile(e.target.value)}
                        maxLength={20}
                        className="w-full px-3 py-2 border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder={t('Enter mobile number')}
                      />
                      {/* The number is the identity, so warn before the sale
                          rather than after two slates already exist. */}
                      {existingMatch && (
                        <p className="text-sm text-amber-900 mt-1">
                          {t('That number is already on file as')}{' '}
                          <button
                            type="button"
                            onClick={() => chooseCustomer(existingMatch)}
                            className="font-medium underline hover:no-underline"
                          >
                            {existingMatch.name}
                          </button>
                          {t('. This bill will be added to their account.')}
                        </p>
                      )}
                    </div>

                    {customers.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setAddingNew(false)}
                        className="text-sm text-amber-800 underline hover:no-underline"
                      >
                        {t('Pick an existing customer instead')}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder={t('Search by name or mobile')}
                      />
                    </div>

                    <div className="max-h-44 overflow-y-auto rounded-md border border-amber-200 bg-white divide-y divide-amber-100">
                      {customersLoading ? (
                        <p className="p-3 text-sm text-gray-500">{t('Loading customers...')}</p>
                      ) : matchingCustomers.length === 0 ? (
                        <p className="p-3 text-sm text-gray-500">
                          {t('No customer matches "{query}".', { query: customerSearch })}
                        </p>
                      ) : (
                        matchingCustomers.map((customer) => (
                          <button
                            key={customer.mobile}
                            type="button"
                            onClick={() => chooseCustomer(customer)}
                            className="w-full text-left px-3 py-2 min-h-[44px] hover:bg-amber-50 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-gray-900 truncate">
                                {customer.name}
                              </span>
                              {customer.outstanding > 0 && (
                                <span className="text-sm text-amber-800 flex-shrink-0">
                                  Rs {customer.outstanding.toFixed(2)}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-gray-600">{customer.mobile}</span>
                          </button>
                        ))
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setAddingNew(true);
                        setDueName('');
                        setDueMobile(customerSearch.replace(/\D/g, ''));
                      }}
                      className="flex items-center text-sm text-amber-900 font-medium hover:underline"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      {t('New customer')}
                    </button>
                  </>
                )}

                <p className="text-xs text-amber-800">
                  {t('Rs {amount} goes on account and stays outstanding until it is settled from the Dues page.', {
                    amount: finalAmount.toFixed(2)
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cafe-500 transition-colors"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={handleComplete}
              disabled={isProcessing || !canComplete}
              className="flex-1 px-4 py-2 bg-cafe-600 text-white rounded-md hover:bg-cafe-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cafe-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('Processing...')}
                </div>
              ) : isDue ? (
                t('Put on Account')
              ) : (
                t(isBulk ? 'Complete Orders' : 'Complete Order')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;